import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button, Flex, Message, Modal, Text } from '@workwhile/ui'
import { useIndeedFlexShiftActionsContextValue } from '../context/IndeedFlexShiftActionsProvider'
import { SpecificWorkerIdsField } from './SpecificWorkerIdsField'

export const createIndeedFlexShiftFormSchema = z.object({
  specificWorkerIds: z.array(z.number().int().positive()).default([]),
})

export type CreateIndeedFlexShiftForm = z.infer<
  typeof createIndeedFlexShiftFormSchema
>

type CreateIndeedFlexShiftButtonProps = {
  ifClientId: number
  ifShiftId: number
}

export const CreateIndeedFlexShiftButton = ({
  ifClientId,
  ifShiftId,
}: CreateIndeedFlexShiftButtonProps) => {
  const {
    actions: { createIndeedFlexShift, resetCreateIndeedFlexShift },
    state: { createIndeedFlexShiftMutationState },
  } = useIndeedFlexShiftActionsContextValue()

  const [isOpen, setIsOpen] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateIndeedFlexShiftForm>({
    resolver: zodResolver(createIndeedFlexShiftFormSchema),
    defaultValues: {
      specificWorkerIds: [],
    },
  })

  useEffect(() => {
    if (!createIndeedFlexShiftMutationState.isSuccess) {
      return
    }

    setIsOpen(false)
    reset()
    resetCreateIndeedFlexShift()
  }, [
    createIndeedFlexShiftMutationState.isSuccess,
    reset,
    resetCreateIndeedFlexShift,
  ])

  const handleClick = () => {
    resetCreateIndeedFlexShift()
    reset()
    setIsOpen(true)
  }

  const handleCancel = () => {
    resetCreateIndeedFlexShift()
    reset()
    setIsOpen(false)
  }

  const onSubmit = handleSubmit((data) => {
    createIndeedFlexShift({
      ifClientId,
      ifShiftId,
      specificWorkerIds:
        data.specificWorkerIds.length > 0 ? data.specificWorkerIds : undefined,
    })
  })

  return (
    <>
      <Button
        variant="secondary"
        kind="small"
        onClick={handleClick}
        style={{ padding: '0 0.5rem' }}
        disabled={createIndeedFlexShiftMutationState.isPending}
        loading={createIndeedFlexShiftMutationState.isPending}
      >
        <Text fontSize="12px">Create shift</Text>
      </Button>
      {isOpen ? (
        <Modal open onClose={handleCancel} title="Create WorkWhile shift">
          <Text mb={2}>
            Creates a WorkWhile shift for Indeed Flex shift {ifShiftId}.
          </Text>
          {createIndeedFlexShiftMutationState.isError &&
          createIndeedFlexShiftMutationState.errorMessage ? (
            <Message variant="error" mb={3}>
              {createIndeedFlexShiftMutationState.errorMessage}
            </Message>
          ) : null}
          <form onSubmit={onSubmit}>
            <SpecificWorkerIdsField
              control={control}
              name="specificWorkerIds"
              errorMessage={errors.specificWorkerIds?.message}
            />
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              gap={2}
              mt={3}
            >
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createIndeedFlexShiftMutationState.isPending}
                disabled={createIndeedFlexShiftMutationState.isPending}
              >
                Create shift
              </Button>
            </Flex>
          </form>
        </Modal>
      ) : null}
    </>
  )
}
